'use client';

import MaterialIcons from '@/components/MaterialIcons/materialIcons';
import { Button } from '@/components/ui/button';
import { formatDate } from '@/helpers/date-format';
import { getTotalCourseDuration } from '@/lib/utils/duration';
import { QuizItem } from '@/store/assessment-store';
import Image from 'next/image';
import Link from 'next/link';
import { useMemo } from 'react';
import { ResultsHeader } from './components/ResultsHeader';
import { AssessmentConfig, AssessmentResult, Course, Question, Topic, User } from './types';

interface AssessmentResultsProps {
  result: AssessmentResult;
  assessment: AssessmentConfig;
  assessmentName: string;
  learningPathUrl: string;
  updatedAt: string;
  totalQuestions: number;
  topicsData: Topic[];
  courses: Course[];
  courseDetails?: Array<{
    uid: string;
    title: string;
    url: string;
    thumbnail?: { url: string };
    lesson_details?: any;
    right_rail?: { expertise_levels?: string };
  }>;
  questions?: Question[];
  quiz?: QuizItem[];
  user: User | null;
  achievementType?: 'certificate' | 'badge' | 'none';
  certificateData?: any;
  onRetake: () => void;
  /** When true (e.g. learning path in-page view): score card + accuracy section only; no right sidebar */
  compact?: boolean;
}

interface CourseDetails {
  uid: string;
  title: string;
  url: string;
  thumbnail?: {
    url: string;
  };
  duration?: string;
  type?: string;
}

export function AssessmentResults({
  result,
  assessment,
  assessmentName,
  learningPathUrl,
  updatedAt,
  totalQuestions,
  topicsData,
  courses,
  courseDetails = [],
  questions = [],
  quiz = [],
  user,
  achievementType = 'none',
  certificateData,
  onRetake,
  compact = false,
}: AssessmentResultsProps) {
  const percentageAchieved =
    result?.percentageAchieved ?? result?.attemptLog?.at(-1)?.percentageAchieved ?? 0;
  const totalMarks = result?.totalMarks ?? result?.attemptLog?.at(-1)?.totalMarks ?? 0;
  const totalQuestionsCount = result?.totalQuestions ?? result?.attemptLog?.at(-1)?.totalQuestions ?? totalQuestions;
  const incorrectCount = totalQuestionsCount - totalMarks;
  const passingCriteria = assessment?.passing_criteria || 70;
  const passed = result?.isPassed ?? (percentageAchieved >= passingCriteria);
  const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || 'You';
  const attemptNumber = (result?.attemptLog?.length ?? 1);
  const attemptOrdinal = attemptNumber === 1 ? '1st' : attemptNumber === 2 ? '2nd' : attemptNumber === 3 ? '3rd' : `${attemptNumber}th`;
  const completedAt = result?.lastAttemptedAt ?? (result as any)?.attemptLog?.at(-1)?.attemptedAt;

  // Use only API course-wise data (no fallback derivation)
  const resultAny = result as { course_wise_results?: unknown[] } | undefined;
  const lastAttemptAny = result?.attemptLog?.at(-1) as { course_wise_results?: unknown[] } | undefined;
  const rawCourseWise =
    result?.courseWiseResults ??
    resultAny?.course_wise_results ??
    result?.attemptLog?.at(-1)?.courseWiseResults ??
    lastAttemptAny?.course_wise_results;

  const courseWiseResultsList = useMemo(() => {
    if (!Array.isArray(rawCourseWise)) return [];
    return rawCourseWise.map((item: unknown) => {
      const o = item as Record<string, unknown>;
      return {
        courseUid: String(o?.courseUid ?? o?.course_uid ?? ''),
        correct: Number(o?.correct ?? 0),
        incorrect: Number(o?.incorrect ?? 0),
        total: Number(o?.total ?? 0),
      };
    });
  }, [rawCourseWise]);

  return (
    <div className={compact ? 'mx-auto w-full bg-surface-2' : 'section bg-surface-2'}>
      <div className="mx-auto w-full">
        <div className="w-full flex flex-col">
          {/* Main Content - full width (no empty right sidebar) */}
          <div className="flex-1 flex flex-col">
            {/* Header (hidden in compact/LP view) */}
            {!compact && (
              <ResultsHeader
                title={assessmentName}
                type="assessment"
                learningPathUrl={learningPathUrl}
                updatedAt={updatedAt}
                questionIndex={0}
                totalQuestions={totalQuestionsCount}
                isStandaloneCourse={false}
              />
            )}

            {/* Summary Box - dark theme, centered (matches design) */}
            <div className="bg-surface-1 rounded-lg p-4 md:p-8 flex flex-col gap-4">
              <div className="rounded-lg p-6 md:p-10 flex flex-col gap-8 items-center text-center bg-amethyst-dark">
                {/* Top: congrats / need to pass (when applicable) + result line + attempt */}
                <div className="flex flex-col gap-2 w-full items-center">
                  {passed && (
                    <p className="cs-h4 text-text-default-strong">
                      Congrats, {displayName}! You Passed 🎉
                    </p>
                  )}
                  {!passed && (
                    <p className="cs-h4 text-text-default-strong">
                      You answered {totalMarks} of {totalQuestionsCount} questions correctly

                    </p>
                  )}
                  {/* <p className="cs-h4 text-text-default-strong">
                    You answered {totalMarks} of {totalQuestionsCount} questions correctly
                  </p> */}
                  <p className="cs-m-body text-text-default-light">
                    This was your {attemptOrdinal} attempt
                  </p>
                  {completedAt && (
                    <p className="cs-caption text-text-default-light">
                      Completed: {formatDate(completedAt)}
                    </p>
                  )}
                </div>

                {/* Three metrics: stacked on mobile, row with dividers on md+ */}
                <div className="flex flex-col md:flex-row items-stretch w-full max-w-full overflow-hidden">
                  <div className="flex flex-col items-center justify-center flex-1 min-w-0 py-4 px-4 border-b md:border-b-0 md:border-l border-border-medium first:md:border-l-0">
                    <p className="text-amethyst-accent font-bold text-2xl md:text-3xl tabular-nums">
                      {totalMarks}/{totalQuestionsCount}
                    </p>
                    <p className="cs-caption text-text-default-strong mt-1">Correct</p>
                  </div>
                  <div className="flex flex-col items-center justify-center flex-1 min-w-0 py-4 px-4 border-b md:border-b-0 md:border-l border-border-medium">
                    <p className="text-amethyst-accent font-bold text-2xl md:text-3xl tabular-nums">
                      {incorrectCount}
                    </p>
                    <p className="cs-caption text-text-default-strong mt-1">Incorrect</p>
                  </div>
                  <div className="flex flex-col items-center justify-center flex-1 min-w-0 py-4 px-4 md:border-l border-border-medium">
                    <p className="text-amethyst-accent font-bold text-2xl md:text-3xl tabular-nums">
                      {percentageAchieved}%
                    </p>
                    <p className="cs-caption text-text-default-strong mt-1">Percentage</p>
                    <p className="cs-caption text-text-default-light mt-0.5">
                      {passingCriteria}% required to pass
                    </p>
                  </div>
                </div>

                {/* Actions: Retake link + View certificate button */}
                <div className="flex flex-col sm:flex-row gap-4 items-center justify-center w-full">
                  <button
                    type="button"
                    onClick={onRetake}
                    className="inline-flex cursor-pointer items-center gap-2 cs-button px-4 py-2 bg-transparent text-text-default-light hover:bg-surface-5 hover:text-text-default-strong transition-colors"
                  >
                    Retake assessment
                    <MaterialIcons iconName="restart_alt" className="!text-[20px]" />
                  </button>
                  {passed && achievementType === 'certificate' && (
                    certificateData?.uid ? (
                      <Button
                        variant="PrimaryFillButton"
                        className="cs-button bg-amethyst-accent hover:opacity-90 text-onAction-strong w-full sm:w-auto"
                        asChild
                      >
                        <Link
                          href={`/certificate/${certificateData.uid}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View certificate
                        </Link>
                      </Button>
                    ) : (
                      <Button
                        variant="PrimaryFillButton"
                        className="cs-button w-full sm:w-auto"
                        disabled
                        aria-label="Certificate is being loaded"
                      >
                        Loading certificate...
                      </Button>
                    )
                  )}
                  {passed && achievementType === 'badge' && result?.badgeData?.uid && (
                    <Button
                      variant="PrimaryFillButton"
                      className="cs-button bg-amethyst-accent hover:opacity-90 text-onAction-strong w-full sm:w-auto"
                      asChild
                    >
                      <Link
                        href={`/profile?tab=achievements${result.badgeData?.uid ? `&badgeid=${result.badgeData.uid}` : ''}`}
                      >
                        View badge
                      </Link>
                    </Button>
                  )}
                </div>
              </div>

              {courseWiseResultsList.length > 0 && (
                <>
                  <div className="border-t border-border-medium"></div>
                  <div className="flex flex-col gap-4">
                    <h2 className=" cs-h5">
                      Course Accuracy Breakdowns
                    </h2>
                    {courseWiseResultsList.map((item, i: number) => {
                      const accuracy = item.total > 0 ? Math.floor((item.correct / item.total) * 100) : 0;

                      const course = courses?.find(
                        (c) => c.uid === item.courseUid || c.title === item.courseUid
                      ) || courseDetails?.find(
                        (c) => c.uid === item.courseUid || c.title === item.courseUid
                      );
                      const courseDetail = courseDetails?.find(
                        (c) => c.uid === item.courseUid || c.title === item.courseUid
                      );

                      const courseTitle = course?.title ?? courseDetail?.title ?? item.courseUid ?? '';
                      const courseUrlRaw = course?.url ?? courseDetail?.url ?? '';
                      const courseThumbnail =
                        (courseDetail?.thumbnail?.url ?? (course as { thumbnail?: { url?: string } })?.thumbnail?.url ?? (courseDetail as { thumbnail?: { url?: string } })?.thumbnail?.url) || undefined;
                      const courseDuration = courseDetail ? getTotalCourseDuration(courseDetail) : '';

                      const lpBase = learningPathUrl.startsWith('/learning-paths')
                        ? learningPathUrl.replace(/\/$/, '')
                        : `/learning-paths${learningPathUrl.startsWith('/') ? learningPathUrl : `/${learningPathUrl}`}`.replace(/\/$/, '');
                      const viewCourseHref =
                        !courseUrlRaw || courseUrlRaw.startsWith('http')
                          ? ''
                          : `${lpBase}${courseUrlRaw.startsWith('/') ? courseUrlRaw : `/${courseUrlRaw}`}`;

                      return (
                        <div
                          key={i}
                          className="flex flex-col lg:flex-row h-auto lg:h-[90px] bg-surface-3"
                        >
                        {/* Column 1: Image */}
                        <div className="relative w-full lg:w-[160px] h-[280px] lg:h-full flex-shrink-0 overflow-hidden card-hover-zoom">
                          {courseThumbnail ? (
                            <Image
                              src={courseThumbnail}
                              alt={courseTitle}
                              fill
                              className="object-cover card-zoom-image"
                            />
                          ) : (
                            <div className="w-full h-full bg-surface-4 flex items-center justify-center">
                              <span className="text-icon-subtle text-4xl">📚</span>
                            </div>
                          )}
                        </div>

                        {/* Content area with padding */}
                        <div className="flex-1 flex flex-col lg:flex-row items-start lg:items-center gap-4 lg:gap-6 min-w-0 p-4 lg:p-6">
                          {/* Column 2: Course title */}
                          <div className="flex-1 flex flex-col justify-center min-w-0 w-full lg:w-auto">
                            <h6 className="cs-h6 line-clamp-1">
                              {courseTitle}
                            </h6>
                            <div className="flex items-center gap-2">
                              <span className="cs-caption">Course</span>
                              {courseDuration && (
                                <>
                                  <span className="cs-caption border-l border-border-medium pl-2">
                                    {courseDuration}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>

                          {/* Column 3: Accuracy bar and percentage */}
                          <div className="flex flex-row lg:flex-col gap-3 lg:gap-1 items-center lg:items-start w-full lg:w-auto lg:flex-1">
                            {/* Accuracy bar */}
                            <div className="flex-1 lg:flex-initial flex flex-col justify-center gap-1 min-w-0 w-full lg:w-[200px]">
                              <div className="h-1 bg-surface-5 overflow-hidden">
                                <div
                                  className="learning-path-progress-bar h-full transition-all duration-300"
                                  style={{
                                    width: `${Math.max(0, Math.min(100, accuracy))}%`,
                                  }}
                                />
                              </div>
                              <span className="cs-caption">
                                <span className="cs-percentage-count">{accuracy}%</span> Accuracy
                              </span>
                            </div>
                          </div>

                          {/* Column 4: View course link - hidden on mobile; href is learning-paths/{slug}/{courseUrl} */}
                          {viewCourseHref && (
                            <div className="hidden lg:flex items-center shrink-0">
                              <Link
                                href={viewCourseHref}
                                className="additional-type-s-button hover:text-text-default-strong transition-colors whitespace-nowrap"
                              >
                                View course
                              </Link>
                            </div>
                          )}
                        </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
